import { Box, Divider, Typography, FormHelperText } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AsyncBackdropSubmit from '../../components/AsyncBackdropSubmit';
import BackButton from '../../components/Buttons/BackButton';
import CreateProfessionalSteps from '../../components/FormContainers/CreateProfessional/CreateProfessionalSteps';
import { IProfForm } from '../../components/FormContainers/CreateProfessional/type';
import { MapPoint } from '../../components/Maps/types';
import FormSkeleton from '../../components/Skeletons/FormSkeleton';
import i18n from '../../i18n';
import { AppleTitleNamingKeys, Ilevels } from '../../lib/generalTypes';
import { GetSortOrder } from '../../lib/helperFuncs';
import { IntExt } from '../../lib/permissions';
import { createApplication, getApplicationFiles, getProfessionalDetails, getProfessionalsDropdownOptions, updateProfessional } from '../../lib/static/professionals/fetchers';
import { createProfessionalInitialValues } from '../../lib/static/professionals/initialValues';
import UserService from '../../services/UserService';
import Theme from '../../Themes/main.theme';
import PreviewProfessional from './PreviewProfessional';
import { useUserDbInfo } from '../../hooks/UserInfo';
import { useFromAccessLogic } from '../../hooks/useFromAccessLogic';

const CreateProfessional = () => {
  const [data, setData] = React.useState<any>();
  const [renderForm, setRenderForm] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isLoadingParametrics, setIsLoadingParametrics] = React.useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [level1Options, setlevel1Options] = React.useState<Ilevels | []>([]);
  const [languages, setLanguages] = React.useState<Ilevels | []>([]);
  const [regions, setRegions] = React.useState<Ilevels | []>([]);
  const [regionsOnly, setRegionsOnly] = React.useState<Ilevels | []>([]);
  const [specialtiesLevel2, setSpecialtiesLevel2] = React.useState<Ilevels | []>([]);
  const [onSubmitLoading, setOnSubmitLoading] = React.useState<boolean>(false);

  const { availableForms, isLoading: isFormPublishedLoading } =
  useFromAccessLogic(AppleTitleNamingKeys.ProfessionalRegister);


  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const is_external = UserService.hasRole([IntExt.External]);

  let taxis_user: any = {};
  let taxis_user_loading = false;

  document.title= t('Αίτημα Καταχώρισης Επαγγελματία / Επιχείρησης');

  const convertJsonBodyToFormData = (body: IProfForm) => {
    const _media: string[] = [];
    const _attachments: string[] = [];
    
    const formData = new FormData();
    if (body?.thumbnail && typeof body?.thumbnail !== 'string') {      
      if (body?.thumbnail.length === 0) {
        delete body.thumbnail;
      } else {
        formData.append('file',
          new Blob(body?.thumbnail, {
            type: body?.thumbnail[0].type
          }), 
          encodeURI(body?.thumbnail[0].name)
        );
      }
      delete body.thumbnail;
    }
    if (body?.resume && typeof body?.resume !== 'string') {
      if (body?.resume.length === 0) {
        delete body.resume;
      } else {
        formData.append('resume',
          new Blob(body?.resume, { 
            type: body?.resume[0].type
          }),
          encodeURI(body?.resume[0].name)
        );
      }
      delete body.resume;
    }
    if (Array.isArray(body?.media)) {
      body?.media.map((x: File) => {
        if (typeof x === 'string') {
          _media.push(x);
        } else 
          formData.append('media',
            new Blob([x], {
              type: x.type
            }),
            encodeURI(x.name)
          );
      });
      delete body.media;
    }
    if (Array.isArray(body?.attached_files)) {
      body?.attached_files.map((x: File) => {
        if (typeof x === 'string') {
          _attachments.push(x);
        } else {
          formData.append('attachments',
            new Blob([x], {
              type: x.type
            }),
            encodeURI(x.name)
          );
        }
      });
      delete body.attached_files;
    }
    formData.append('form', 
      JSON.stringify({
        ...body,
        ...(body.city 
          ? { city: body.city }
          : { city: null }
        ),
        ...(_media.length > 0 && {media: _media}),
        ...(_attachments.length > 0 && {attachments: _attachments}) 
      })
    );
    return formData;
  };

  const onSubmit = async (values: IProfForm, geolocation: MapPoint) => {
    
    setOnSubmitLoading(true);
    const addedValues = {
      ...values,
      specialty: [...new Set([...values?.specialty_lvl2?.map(x => x.parent_object) || []])],
      latitude: geolocation?.lat
        ? String(geolocation?.lat)
        : null,
      longitude: geolocation?.lng
        ? String(geolocation?.lng)
        : null};
    const formData = convertJsonBodyToFormData(addedValues);
        
    try {
      const resp = searchParams.has('edit')
        ? await updateProfessional(data.id, formData)
        : await createApplication(formData);
            
      const files = await getApplicationFiles(resp.data.application_id);
      
      localStorage.setItem('professionalId', resp.data.id.toString());
      searchParams.set('preview', 'true');
      searchParams.delete('edit');
      setSearchParams(searchParams);
      
      setData({...resp.data,
        media: files.data.media,
        thumbnail: files.data.thumbnail,
        resume: files.data.resume,
        attached_files: files.data.attached_files
      });

      setRenderForm(false);
      
      enqueueSnackbar(t('Το αίτημα καταχώρισης επαγγελματία / επιχείρησης αποθηκεύτηκε στα Πρόχειρα!'), { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('Το αίτημα καταχώρισης επαγγελματία / επιχείρησης απέτυχε, παρακαλώ προσπαθήστε ξανά.'), { variant: 'error' });
    }
    setOnSubmitLoading(false);
  };
  
  const getParametrics = async () => {
    setIsLoadingParametrics(true);
    const { 
      services, languages, regions, regions_only, specialty_level_2
    } = await getProfessionalsDropdownOptions();    
    
    const services_ = services.map((service: any) => {
      return { 
        key: t(service.key),
        value: service.key
      };
    });

    const regions_ = regions.map((region: any) => {
      return {
        key: t(region.key),
        value: (region.value)
      };
    });

    const lanuages_ = languages.map((language: any) => {
      return {
        key: t(language.key),
        value: (language.value)
      };
    });

    const regionsOnly_ = regions_only.map((reg: any) => {
      return {
        key: t(reg.key),
        value: (reg.value)
      };
    });

    const options_level_2 = specialty_level_2.map((option: any) => {
      return {
        key: t(option.key),
        value: option.value,
        parent: t(option.parent)
      };
    }).filter((value: any) => value);
    
    setlevel1Options(i18n.language === 'en'
      ? services_.sort(GetSortOrder('key'))
      : services_.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
    setLanguages(i18n.language === 'en'
      ? lanuages_.sort(GetSortOrder('key'))
      : lanuages_.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
    setRegions(i18n.language === 'en'
      ? regions_.sort(GetSortOrder('key'))
      : regions_.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
    setRegionsOnly(i18n.language === 'en'
      ? regionsOnly_.sort(GetSortOrder('key'))
      : regionsOnly_.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
    setSpecialtiesLevel2(i18n.language === 'en'
      ? options_level_2.sort(GetSortOrder('parent'))
      : options_level_2.sort((a: any,b: any) => a.parent.localeCompare(b.parent, 'el')));
    setIsLoadingParametrics(false);
  };


  React.useEffect(() => {
    if (is_external && !isFormPublishedLoading) {
      if (!availableForms.is_published) {
        enqueueSnackbar(t('Το αίτημα δεν είναι διαθέσιμο αυτή τη στιγμή.'), { variant: 'info' });
        navigate('/professionals');
      }
    }
  }, [isFormPublishedLoading]);


  const getData = async (id: number) => {
    try {
      setIsLoading(true);
      const resp = await getProfessionalDetails(id);
      setData({
        ...resp.data,
      });
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    getParametrics();
  }, []);
  
  React.useEffect(() => {
    if (
      Object.prototype.hasOwnProperty.call(Object.fromEntries([...searchParams]), 'edit') ||
      Object.prototype.hasOwnProperty.call(Object.fromEntries([...searchParams]), 'preview')
    ){

      const professional_id = localStorage.getItem('professionalId');

      if (professional_id) {
        getData(+professional_id);
        Object.prototype.hasOwnProperty.call(Object.fromEntries([...searchParams]), 'preview') && setRenderForm(false);
      } else {
        searchParams.delete('edit');
        searchParams.delete('preview');
        setSearchParams(searchParams);
        setRenderForm(true);
      }

    } else setRenderForm(true);

  }, []);

  if (UserService.hasRole(['EXTERNAL_TAXISNET']) && !searchParams.has('edit')) {
    const { userDbInfo, loading } = useUserDbInfo();    
    taxis_user = userDbInfo;
    taxis_user_loading = loading;
  }
  
  return (
    <>
      {!isLoadingParametrics
        ? renderForm 
          ? <>
            <AsyncBackdropSubmit isLoading={onSubmitLoading}>
              {t('Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...')}
            </AsyncBackdropSubmit>
            <Box mt={2} mb={2}>
              <BackButton onClick={() => navigate(-1)}/>
            </Box>        
            {is_external
              ? <Typography sx={{my: 2}} variant='h2'
                color={Theme?.labelColor?.main}>{t('Αίτημα Καταχώρισης Επαγγελματία / Επιχείρησης')}
              </Typography>
              : <Typography sx={{my: 2}} variant='h2'
                color={Theme?.labelColor?.main}>{t('Εσωτερικό Αίτημα Καταχώρισης Επαγγελματία / Επιχείρησης')}
              </Typography>}
            <Divider sx={{mb: 5, border: 1, color: Theme?.dividerColor?.main}}/>
            {!isLoading && searchParams.has('edit')
              ? <FormHelperText>
                <Typography
                  variant='body1'
                  fontStyle='italic'
                  sx={{
                    marginTop: '16px',
                    fontWeight: 'bolder',
                  }}
                >
                  {t('Πρέπει να συμπληρωθούν όλα τα υποχρεωτικά πεδία, ώστε να κάνετε οριστική υποβολή του αιτήματός σας.')}
                </Typography>
              </FormHelperText>
              : null
            }
            {isLoading || taxis_user_loading
              ? <FormSkeleton lines={6} />
              : <CreateProfessionalSteps
                isLoading={isLoadingParametrics}
                initialValues={{
                  ...createProfessionalInitialValues,
                  ...data,
                  ...(taxis_user && Object.keys(taxis_user).length > 0 && {
                    ...(taxis_user?.nomiki
                      ? {
                        title: taxis_user?.last_name
                      }
                      : {
                        first_name: taxis_user?.first_name,
                        last_name: taxis_user?.last_name,
                      }),
                    afm: taxis_user?.afm,
                    phone_number: taxis_user?.phone_number,
                    email: taxis_user?.email,
                  })
                }}
                level1Options={level1Options}
                languages={languages}
                regions={regions}
                regionsOnly={regionsOnly}
                specialtiesLevel2={specialtiesLevel2}
                onSubmit={onSubmit}/>
            }
          </>
          : <PreviewProfessional
            isLoading={isLoading || isLoadingParametrics} 
            data={data}
            setRenderForm={setRenderForm}/>
        : <FormSkeleton lines={7} />
      }
    </>
  );
};

export default CreateProfessional;